import { FC, useState, useRef, useEffect } from 'react';
import styles from './SelectMenuFilter.module.scss';
import { useClickOutside } from 'hooks/useOnClickOutside';
import { ReactComponent as IconChewron } from './img/iconChewron.svg';
import { TextWithTooltip } from 'hooks/TextWithTooltip';

interface ListItemTypes {
  name: string;
  key: string;
  count?: number;
}

type SelectMenuFilterProps = {
  active: string;
  lists?: ListItemTypes[];
  setActive: (tab: string) => void;
  localStorageKey?: string;
};

export const SelectMenuFilter: FC<SelectMenuFilterProps> = ({
  active,
  lists,
  setActive,
  localStorageKey = 'ordersFilter',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const clickOutsideRef = useClickOutside(() => setIsOpen(false));
  const rootRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const name = lists?.find((item) => item.key === active)?.name;

  useEffect(() => {
    if (!contentRef.current || !rootRef.current || !name) return;

    const updateFlexBasis = () => {
      const styles = window.getComputedStyle(contentRef.current!);
      const measureEl = document.createElement('span');
      measureEl.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap;';
      measureEl.style.font = styles.font;
      measureEl.textContent = name;

      document.body.appendChild(measureEl);
      const textWidth = measureEl.offsetWidth;
      document.body.removeChild(measureEl);

      const padding =
        parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight) || 32;
      const gap = parseFloat(styles.gap) || 6;
      const iconWidth =
        contentRef.current!.querySelector('svg')?.getBoundingClientRect().width || 20;

      rootRef.current!.style.flexBasis = `${textWidth + padding + gap + iconWidth}px`;
    };

    const rafId = requestAnimationFrame(updateFlexBasis);
    window.addEventListener('resize', updateFlexBasis);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', updateFlexBasis);
    };
  }, [active, lists, name]);

  const Menu = () => {
    return (
      <ul className={styles.list} ref={clickOutsideRef}>
        {lists?.map((item, i) => (
          <li
            className={`${styles.item} ${item.key === active ? styles.item_active : ''}`}
            onClick={() => {
              localStorage.setItem(localStorageKey, item.key);
              setActive(item.key);
              setIsOpen(false);
            }}
            key={i}>
            {item.name}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div ref={rootRef} className={styles.root}>
      <div
        ref={contentRef}
        className={styles.content}
        onClick={() => setIsOpen((prev) => !prev)}>
        <TextWithTooltip
          content={name ? name : ''}
          className={styles.text}
          isMobile={true}
        />
        <IconChewron className={styles.icon} />
      </div>
      {isOpen && <Menu />}
    </div>
  );
};
